import heatmapEndpoints from './endpoints/heatmap.endpoints';

/**
 * Generate a behavioral heatmap for a subject.
 * @param {number} alienId - The alien/subject identifier.
 * @param {number} [resolution=8] - H3 resolution (7=~5km, 8=~1km, 9=~350m, 10=~100m).
 * @param {number} [days=30] - Number of days to include.
 * @returns {Promise<Object|null>} Heatmap data with cells and summary.
 */
export async function generateHeatmap(alienId, resolution = 8, days = 30) {
  try {
    const response = await heatmapEndpoints.generate(alienId, { resolution, days });
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    if (error.response?.status === 204) {
      console.log(`[generateHeatmap] No location data for alien ${alienId}`);
      return null;
    }
    console.error('[generateHeatmap] Error:', error);
    return null;
  }
}

/**
 * Get stored heatmap data for a subject.
 * @param {number} alienId - The alien/subject identifier.
 * @param {number} [resolution=8] - H3 resolution level.
 * @returns {Promise<Object|null>} Heatmap data.
 */
export async function getHeatmap(alienId, resolution = 8) {
  try {
    const response = await heatmapEndpoints.get(alienId, { params: { resolution } });
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      console.log(`[getHeatmap] No heatmap found for alien ${alienId}`);
      return null;
    }
    console.error('[getHeatmap] Error:', error);
    return null;
  }
}

/**
 * Generate a temporal (time-sliced) heatmap for a subject.
 * @param {number} alienId - The alien/subject identifier.
 * @param {string} [sliceType='weekly'] - Time slice type: 'daily', 'weekly', or 'monthly'.
 * @param {number} [days=60] - Number of days to include.
 * @param {number} [resolution=8] - H3 resolution level.
 * @returns {Promise<Object|null>} Temporal heatmap data with slices.
 */
export async function generateTemporalHeatmap(alienId, sliceType = 'weekly', days = 60, resolution = 8) {
  try {
    const response = await heatmapEndpoints.generateTemporal(alienId, { sliceType, days, resolution });
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    if (error.response?.status === 204) {
      console.log(`[generateTemporalHeatmap] No location data for alien ${alienId}`);
      return null;
    }
    console.error('[generateTemporalHeatmap] Error:', error);
    return null;
  }
}

/**
 * Get stored temporal heatmap data for a subject.
 * @param {number} alienId - The alien/subject identifier.
 * @param {string} [sliceType='weekly'] - Time slice type.
 * @param {number} [resolution=8] - H3 resolution level.
 * @returns {Promise<Object|null>} Temporal heatmap data.
 */
export async function getTemporalHeatmap(alienId, sliceType = 'weekly', resolution = 8) {
  try {
    const response = await heatmapEndpoints.getTemporal(alienId, { params: { sliceType, resolution } });
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      console.log(`[getTemporalHeatmap] No temporal heatmap found for alien ${alienId}`);
      return null;
    }
    console.error('[getTemporalHeatmap] Error:', error);
    return null;
  }
}

/**
 * Get color for activity level using a gradient.
 * @param {number} score - The time-weighted activity score.
 * @param {number} maxScore - Maximum score in the dataset.
 * @returns {string} Hex color string.
 */
export function getActivityColor(score, maxScore) {
  if (maxScore <= 0) return '#3b82f6'; // Blue fallback

  const ratio = Math.min(score / maxScore, 1);

  // Color gradient: blue (low) -> yellow -> red (high)
  if (ratio < 0.5) {
    // Blue to yellow
    const t = ratio * 2;
    const r = Math.round(59 + (234 - 59) * t);
    const g = Math.round(130 + (179 - 130) * t);
    const b = Math.round(246 + (8 - 246) * t);
    return `rgb(${r}, ${g}, ${b})`;
  } else {
    // Yellow to red
    const t = (ratio - 0.5) * 2;
    const r = Math.round(234 + (239 - 234) * t);
    const g = Math.round(179 - 179 * t);
    const b = Math.round(8 + (68 - 8) * t);
    return `rgb(${r}, ${g}, ${b})`;
  }
}

/**
 * Get opacity for activity level.
 * @param {number} score - The time-weighted activity score.
 * @param {number} maxScore - Maximum score in the dataset.
 * @returns {number} Opacity value between 0.2 and 0.8.
 */
export function getActivityOpacity(score, maxScore) {
  if (maxScore <= 0) return 0.4;
  const ratio = Math.min(score / maxScore, 1);
  return 0.2 + ratio * 0.6; // Range from 0.2 to 0.8
}

/**
 * Get resolution description.
 * @param {number} resolution - H3 resolution level.
 * @returns {string} Human-readable description.
 */
export function getResolutionDescription(resolution) {
  const descriptions = {
    7: '~5km hexagons',
    8: '~1km hexagons',
    9: '~350m hexagons',
    10: '~100m hexagons',
  };
  return descriptions[resolution] || `Resolution ${resolution}`;
}

/**
 * Format dwell time for display.
 * @param {number} minutes - Dwell time in minutes.
 * @returns {string} Formatted dwell time string.
 */
export function formatDwellTime(minutes) {
  if (!minutes || minutes <= 0) return '0m';

  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);

  if (hours > 0) {
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  return `${mins}m`;
}

/**
 * Get slice type label.
 * @param {string} sliceType - The slice type code.
 * @returns {string} Human-readable label.
 */
export function getSliceTypeLabel(sliceType) {
  const labels = {
    daily: 'Daily',
    weekly: 'Weekly',
    monthly: 'Monthly',
  };
  return labels[sliceType] || sliceType;
}
